import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useInView } from '../../hooks/useInView';

const Emphasis = ({ children }) => (
  <span className="font-semibold text-neutral-800">{children}</span>
);

const STEPS = [
  {
    title: 'Mark the hard detail',
    description: (
      <>
        The hard detail is your weak link, and weak links cause exam failures. By marking it
        explicitly, you&apos;re making the hard detail the{' '}
        <Emphasis>defining characteristic of the topic</Emphasis>, not the familiar details.
      </>
    ),
    imageCaption:
      'Use your performance in practice tests to identify which detail to prioritise. Target the one most likely to cost you marks in the real exam. If a topic has more than one hard detail, start with the most critical one and mark the rest as you go.',
  },
  {
    title: 'Use the detail to find or create a basket',
    description: (
      <>
        Searching for a connection forces you to actively engage with the detail you just marked.
        If you can&apos;t find a basket, create a new one for it.{' '}
        <Emphasis>The goal: every topic is inside a basket.</Emphasis>
      </>
    ),
    imageCaption:
      "Always place a topic inside a basket, even if it's the only one in there. Doing so achieves two things: you actively engage with the hard detail, and you guarantee the topic will resurface when you move on to new ones.",
  },
  {
    titlePlain: "Move to the next topic (don't wait for connections)",
    title: (
      <>
        Move to the next topic{' '}
        <span className="text-base font-medium italic text-neutral-600">
          (don&apos;t wait for connections)
        </span>
      </>
    ),
    description: (
      <>
        This is where the system pays off. When you move to a new topic and find a basket for it,{' '}
        <Emphasis>older topics inside baskets reappear.</Emphasis> You&apos;re not scheduling
        revision. You&apos;re just studying forward, and the old material comes to you.
      </>
    ),
    imageCaption:
      "Notice how Vitamin B12 (inside the Terminal ileum basket) reappears while you're studying a completely different topic. This is revision happening in the background. In traditional study, you'd need a separate crammed session to achieve the same thing.",
  },
  {
    title: 'Master your baskets using Qbanks',
    description: (
      <>
        Open your Qbank and work through questions as normal. When a question relates to a topic in
        Basket, find it and ask: why is it in this basket, and what else is inside? One question
        about a topic becomes <Emphasis>a review of every topic connected to it</Emphasis>,
        including their own hard details. You cover more ground per question than any other revision
        method.
      </>
    ),
    imageCaption:
      'By searching "terminal ileum", you revisit not just Vitamin B12 but every topic connected to it. As exam day approaches and the pressure builds, this kind of efficiency is a game changer.',
  },
];

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

const getStepTitleText = (step) =>
  step.titlePlain ?? (typeof step.title === 'string' ? step.title : '');

const StepLabel = ({ stepNumber }) => (
  <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-cranberry-800">
    <span className="w-2 h-2 rounded-full bg-cranberry-700 flex-shrink-0" aria-hidden="true" />
    Step {stepNumber}
  </span>
);

const ImagePreviewModal = ({ image, onClose }) => {
  useEffect(() => {
    if (!image) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [image, onClose]);

  if (!image) return null;

  const captionId = image.caption ? 'hiw-preview-caption' : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75"
      role="dialog"
      aria-modal="true"
      aria-label={image.alt}
      aria-describedby={captionId}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/90 text-neutral-900 border-2 border-neutral-900 hover:bg-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cranberry-700"
        aria-label="Close image preview"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        className="max-w-5xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-xl border-2 border-neutral-900 p-3 sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt}
          className="w-full h-auto max-h-[85vh] object-contain mx-auto block"
        />
        {image.caption && (
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <p
              id="hiw-preview-step-label"
              className="text-xs font-bold uppercase tracking-widest text-cranberry-800 mb-2"
            >
              Step {image.stepNumber}
            </p>
            <p id={captionId} className="text-sm text-[#475569] leading-relaxed">
              {image.caption}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const StepCard = ({ step, imageSrc, stepNumber, show, animate, delayMs, onImageClick }) => (
  <article
    className={`grid grid-cols-1 md:grid-cols-[minmax(0,260px)_1fr] border-2 border-[#111111] rounded-[14px] overflow-hidden ${
      !show ? 'opacity-0' : ''
    } ${animate ? 'animate-hiw-fade-up' : ''}`}
    style={animate ? { animationDelay: `${delayMs}ms` } : undefined}
  >
    <div className="p-4 md:p-5">
      <StepLabel stepNumber={stepNumber} />
      <h3 className="text-lg font-semibold text-[#0F172A] mt-3 mb-2">{step.title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{step.description}</p>
    </div>
    <div className="bg-[#fdf2f4] border-[#111111] border-t-2 md:border-t-0 md:border-l-2 p-1 flex items-center justify-center min-h-0">
      <button
        type="button"
        onClick={onImageClick}
        className="w-full cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cranberry-700 rounded"
        aria-label={`View larger image for step ${stepNumber}: ${getStepTitleText(step)}`}
      >
        <img
          src={imageSrc}
          alt=""
          width={800}
          height={450}
          className="w-full h-auto block object-contain pointer-events-none"
        />
      </button>
    </div>
  </article>
);

const HEADLINE_SEGMENTS = [
  { id: 'detail', type: 'word', label: 'Detail' },
  { id: 'arrow-1', type: 'arrow', label: '→' },
  { id: 'basket', type: 'word', label: 'Basket' },
  { id: 'arrow-2', type: 'arrow', label: '→' },
  { id: 'master', type: 'word', label: 'Master' },
];

/** ms between each headline segment starting (Detail → arrow → Basket → arrow → Master) */
const HEADLINE_SEGMENT_INTERVAL_MS = 400;

const HeadlineSequence = ({ show, animate, prefersReducedMotion }) => {
  const getSegmentClassName = (type) => {
    const colorClass = type === 'word' ? 'text-cranberry-700' : 'text-neutral-500 origin-left';
    const base = `inline-block ${colorClass}`;

    if (prefersReducedMotion) return base;
    if (!show) return `${base} opacity-0`;
    if (!animate) return base;

    return type === 'arrow'
      ? `${base} animate-hiw-arrow-draw`
      : `${base} animate-hiw-fade-up`;
  };

  const getSegmentStyle = (index) => {
    if (!animate) return undefined;
    return { animationDelay: `${index * HEADLINE_SEGMENT_INTERVAL_MS}ms` };
  };

  const sublineClassName = () => {
    const base = 'mt-3 text-lg italic text-[#475569]';
    if (prefersReducedMotion) return base;
    if (!show) return `${base} opacity-0`;
    if (!animate) return base;
    return `${base} animate-hiw-fade-up`;
  };

  const sublineStyle = animate
    ? { animationDelay: `${HEADLINE_SEGMENTS.length * HEADLINE_SEGMENT_INTERVAL_MS}ms` }
    : undefined;

  return (
    <>
      <h2
        id="how-it-works-heading"
        className="text-3xl font-bold text-[#0F172A] leading-tight flex flex-wrap justify-center items-center gap-x-1"
      >
        {HEADLINE_SEGMENTS.map((segment, index) => (
          <span
            key={segment.id}
            className={getSegmentClassName(segment.type)}
            style={getSegmentStyle(index)}
            aria-hidden={segment.type === 'arrow' ? true : undefined}
          >
            {segment.label}
          </span>
        ))}
      </h2>
      <p className={sublineClassName()} style={sublineStyle}>
        Start with the detail. The basket follows.
      </p>
    </>
  );
};

/**
 * How Basket works — 4-step workflow section for the landing page.
 * @param {{ images: [string, string, string, string] }} props
 */
const HowItWorks = ({ images }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const motionEnabled = !prefersReducedMotion;

  const [headlineRef, headlineInView] = useInView({ enabled: motionEnabled });
  const [cardsRef, cardsInView] = useInView({ enabled: motionEnabled });
  const [expandedImage, setExpandedImage] = useState(null);

  const cardsShow = cardsInView || prefersReducedMotion;
  const cardsAnimate = cardsInView && motionEnabled;

  const handleClosePreview = useCallback(() => setExpandedImage(null), []);

  if (process.env.NODE_ENV === 'development' && images?.length !== 4) {
    console.warn('HowItWorks expects exactly 4 images.');
  }

  return (
    <>
    <section
      id="how-it-works"
      className="w-full mb-8 sm:mb-12 lg:mb-16 scroll-mt-20 sm:scroll-mt-24"
      aria-labelledby="how-it-works-heading"
    >
      <div className="w-full">
        <header ref={headlineRef} className="mb-8 text-center">
          <p className="text-base font-bold uppercase tracking-widest text-cranberry-800 mb-4">
            The workflow
          </p>
          <HeadlineSequence
            show={headlineInView || prefersReducedMotion}
            animate={headlineInView && motionEnabled}
            prefersReducedMotion={prefersReducedMotion}
          />
        </header>

        <div ref={cardsRef} className="flex flex-col gap-3">
          {STEPS.map((step, index) => (
            <StepCard
              key={index}
              step={step}
              imageSrc={images[index] ?? ''}
              stepNumber={index + 1}
              show={cardsShow}
              animate={cardsAnimate}
              delayMs={index * 100}
              onImageClick={() =>
                setExpandedImage({
                  src: images[index] ?? '',
                  alt: `Step ${index + 1}: ${getStepTitleText(step)}`,
                  stepNumber: index + 1,
                  caption: step.imageCaption,
                })
              }
            />
          ))}
        </div>
      </div>
    </section>
    <ImagePreviewModal image={expandedImage} onClose={handleClosePreview} />
    </>
  );
};

export default HowItWorks;
